import { fc } from "@chatbotgang/etude/react/fc";
import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import type { FC } from "react";
import React, { useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { ExternalLink } from "@/components/ExternalLink";
import { InfoBox } from "@/components/InfoBox";
import { Modal, type ModalProps } from "@/components/Modal";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import {
  ProfileUnificationWarning,
  useProductNotActivated,
} from "@/features/unify-scope-setting/ProfileUnificationWarning";
import { UnifyOrgsProvider } from "@/features/unify-scope-setting/ProfileUnificationWarning/Provider";
import { UnifyKeySelector } from "@/features/unify-scope-setting/UnifyKeySelector";
import { getDefaultUnifyKeys } from "@/features/unify-scope-setting/useDefaultUnifyKeys";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import CresclabImage from "@/statics/cresclab.png";

const cssCard = css`
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.colors.staticFgLine};
  border-radius: 12px;
  overflow: hidden;
`;

const cssCardImageWrapper = css`
  width: 100%;
  height: 88px;
  background-color: ${theme.colors.staticBgContainer};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const cssCardImage = css`
  width: 220px;
  height: 88px;
  object-fit: contain;
`;

const cssCardContent = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  flex: 1;
`;

const cssCardFooter = css`
  display: block;
  padding: 0 16px 16px 16px;
`;

const cssLoadingContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.staticFgLine};
  border-radius: 4px;
`;

const cssConnectedBadge = css`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 20px;
  background-color: ${theme.colors.staticBgPositive};
  color: ${theme.colors.staticFgPositive};
  font-size: 12px;
  font-weight: 500;
`;

const cssTitleContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const cssIcon = css`
  font-size: 1rem;
  color: ${theme.colors.staticFgPlaceholder};
`;

const cssModalImageContainer = css`
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
`;

const cssModalParagraph = css`
  margin-bottom: 16px;
`;

const cssTitleTypography = css`
  font-size: 16px;
`;

const cssDescriptionTypography = css`
  font-size: 14px;
`;

const cssEditButton = css`
  border: 1px solid ${theme.colors.colorBlue60};
  color: ${theme.colors.colorBlue60};
`;

type UnifySettingFormValues = {
  unifyKeys: Array<InterludeTypes["UnificationKeys"]>;
};

/**
 * Design:
 * [Figma](https://www.figma.com/design/k0XwP83RAV16nVdyFx7crb/Omnichannel?node-id=6781-35907&m=dev)
 */
const FinishedState: React.FC = () => (
  <InfoBox
    variant="success"
    transparent
    description={<Trans i18nKey="applications.cdh.merged.title" />}
  />
);

/**
 * Design:
 * [Figma](https://www.figma.com/design/k0XwP83RAV16nVdyFx7crb/Omnichannel?node-id=6781-35865&m=dev)
 */
const MergingState: React.FC = () => (
  <InfoBox
    icon={<MotifIcon un-i-motif="arrow_sync" />}
    variant="primary"
    description={<Trans i18nKey="cdh.unification.merging.label" />}
  />
);

const MergeState: FC = () => {
  const unifyScopeSettingQuery =
    interlude.unifyScopeSetting.useGetUnifyScopeSetting();

  const mergingStateQuery = interlude.unifyScopeSetting.useGetUnifyMergeState();

  if (!mergingStateQuery.isSuccess || !unifyScopeSettingQuery.isSuccess)
    return null;

  if (!unifyScopeSettingQuery.data.isCdhConnected) return null;

  if (mergingStateQuery.data.isFinished) {
    return <FinishedState />;
  }
  return <MergingState />;
};

const InstallCdhModal: FC<
  Pick<ModalProps, "open" | "handleClose"> & { onOk: () => void }
> = ({ open, handleClose, onOk }) => {
  const { t } = useTranslation();
  const notActivatedProducts = useProductNotActivated();
  const orgs = UnifyOrgsProvider.useUnifyOrgsContext();
  return (
    <Modal
      open={open}
      handleClose={handleClose}
      title={t("applications.cdh.modal.title")}
      footer={
        <Stack alignItems="flex-end">
          <Button
            onClick={onOk}
            variant="primary"
            disabled={notActivatedProducts.length !== 0}
          >
            {t("applications.cdh.modal.step1.button.nextStep")}
          </Button>
        </Stack>
      }
    >
      <>
        <div css={cssModalImageContainer}>
          <img src={CresclabImage} alt="Customer Data Hub" />
        </div>
        <Typography variant="body" css={cssModalParagraph}>
          {t("applications.cdh.modal.step1.content.paragraph")}
        </Typography>

        <ProfileUnificationWarning.UnifyScope orgs={orgs} />
        <ProfileUnificationWarning.RequireActivate />
      </>
    </Modal>
  );
};

const InstalCdh: FC = () => {
  const { t } = useTranslation();
  const defaultUnifyKeys = getDefaultUnifyKeys();
  const form = useForm<UnifySettingFormValues>({
    mode: "all",
    defaultValues: {
      unifyKeys: defaultUnifyKeys,
    },
  });
  const [stepOneModalOpen, toggleStepOneModalOpen] = useSwitch(false);
  const [stepTwoModalOpen, toggleStepTwoModalOpen] = useSwitch(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const integrateCdhMutation = interlude.unifyScopeSetting.useIntegrateCdh();

  const goToStepTwo = useHandler(function goToStepTwo() {
    toggleStepOneModalOpen.off();
    toggleStepTwoModalOpen.on();
  });

  const handleCloseStepTwoModal = useHandler(
    function handleCloseStepTwoModal() {
      toggleStepTwoModalOpen.off();
      setCheckboxChecked(false);
      form.reset();
    },
  );

  const onSubmit = useHandler<SubmitHandler<UnifySettingFormValues>>(
    function handleSubmit(values) {
      if (integrateCdhMutation.isPending) return;

      integrateCdhMutation.mutate(
        {
          unifyKeys: values.unifyKeys,
        },
        {
          onSuccess: () => {
            enqueueSnackbar(t("common.updatedSuccessfully"), {
              variant: "success",
            });
            navigate({
              to: "/applications/cdh",
            });
          },
        },
      );
    },
  );

  const meInfoQuery = interlude.auth.useGetMe(undefined);

  const submit = useHandler(function submit() {
    form.handleSubmit(onSubmit)();
  });

  return (
    <>
      <Button
        variant="primary"
        onClick={toggleStepOneModalOpen.on}
        fullWidth={true}
      >
        {t("applications.cdh.button.integrate")}
      </Button>
      <InstallCdhModal
        open={stepOneModalOpen}
        onOk={goToStepTwo}
        handleClose={toggleStepOneModalOpen.off}
      />
      <Modal
        open={stepTwoModalOpen}
        handleClose={handleCloseStepTwoModal}
        title={t("applications.cdh.modal.title")}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="16px">
            <Button variant="plain" onClick={handleCloseStepTwoModal}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              disabled={!checkboxChecked || integrateCdhMutation.isPending}
              loading={integrateCdhMutation.isPending}
            >
              {t("applications.cdh.modal.step2.button.integrate")}
            </Button>
          </Stack>
        }
      >
        <Stack gap="16px">
          <Stack gap="8px">
            <Typography variant="h2" fontWeight={700}>
              {t("applications.cdh.modal.step2.content.title")}
            </Typography>
            <Typography variant="body">
              <Trans
                i18nKey="applications.cdh.modal.step2.content.description"
                components={{
                  strong: <strong />,
                }}
                values={{
                  orgName: meInfoQuery.data?.organization.name ?? "",
                }}
              />
            </Typography>
            <Typography variant="body">
              <ExternalLink
                href={t("applications.cdh.modal.step2.unifyKey.learnMore.link")}
                trailingIcon={true}
              >
                {t("applications.cdh.modal.step2.unifyKey.learnMore")}
              </ExternalLink>
            </Typography>
          </Stack>
          <div>
            <Stack
              direction="row"
              gap="8px"
              alignItems="center"
              marginBottom="8px"
            >
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgTitle}
              >
                {t("applications.cdh.modal.step2.unifyKey")}
              </Typography>
              <MotifIcon un-i-motif="key" css={cssIcon} />
            </Stack>
            <Controller
              name="unifyKeys"
              control={form.control}
              rules={{ required: true }}
              render={({ field, formState }) => (
                <UnifyKeySelector
                  showCount
                  error={Boolean(formState.errors.unifyKeys)}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  helperText={
                    !formState.errors.unifyKeys ? null : (
                      <Trans i18nKey="validation.unification.unifyKeys.min" />
                    )
                  }
                />
              )}
            />
          </div>
          <FormControlLabel
            control={
              <Checkbox
                checked={checkboxChecked}
                onChange={(e) => {
                  setCheckboxChecked(e.target.checked);
                }}
              />
            }
            label={
              <Typography variant="body">
                {t("applications.cdh.modal.step2.consensus")}
              </Typography>
            }
          />
        </Stack>
      </Modal>
    </>
  );
};

export const CdhCard = fc(function Cdh() {
  const { t } = useTranslation();

  const unifyScopeSettingQuery =
    interlude.unifyScopeSetting.useGetUnifyScopeSetting();

  return (
    <Card css={cssCard} variant="outlined">
      <div css={cssCardImageWrapper}>
        <img src={CresclabImage} alt="Customer Data Hub" css={cssCardImage} />
      </div>
      <CardContent css={cssCardContent}>
        <div css={cssTitleContainer}>
          <Typography variant="h3" fontWeight={500} css={cssTitleTypography}>
            {t("applications.cdh.title")}
          </Typography>
          {unifyScopeSettingQuery.data?.isCdhConnected ? (
            <span css={cssConnectedBadge}>{t("common.connected")}</span>
          ) : null}
        </div>
        <Typography variant="body" css={cssDescriptionTypography}>
          {t("applications.cdh.description")}
        </Typography>
        <ExternalLink
          href={t("applications.cdh.learnMore.link")}
          trailingIcon={true}
        >
          {t("common.learnMore")}
        </ExternalLink>
        <MergeState />
      </CardContent>
      <div css={cssCardFooter}>
        {unifyScopeSettingQuery.isPending ? (
          <div css={cssLoadingContainer}>
            <CircularProgress size={20} />
          </div>
        ) : unifyScopeSettingQuery.data?.isCdhConnected ? (
          <Link to="/applications/cdh">
            <Button variant="plain" fullWidth={true} css={cssEditButton}>
              {t("common.manage", "Manage")}
            </Button>
          </Link>
        ) : (
          <ProfileUnificationWarning.Provider>
            <InstalCdh />
          </ProfileUnificationWarning.Provider>
        )}
      </div>
    </Card>
  );
});
